import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarkdownView from "@/components/MarkdownView";
import { ArrowRight, ChevronLeft, Lock, ExternalLink, FileDown, BookOpen, Clock } from "lucide-react";

export default function ResearchIdeaDetail() {
  const { id } = useParams();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(`/research-ideas/${id}`)
      .then((r) => { if (!cancelled) setIdea(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center kb-mono text-xs text-kb-ink-muted">Loading…</div>;
  }
  if (err || !idea) {
    return (
      <div className="min-h-screen bg-kb-bg">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center">
          <div className="kb-mono text-xs text-kb-ink-muted">{err || "Idea not found"}</div>
          <Link to="/research-ideas" className="kb-btn-outline text-sm mt-4 inline-flex"><ChevronLeft className="w-3.5 h-3.5" /> Back to catalogue</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="research-idea-detail-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 pt-10 pb-24">
        <Link to="/research-ideas" className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-1 hover:text-kb-primary" data-testid="idea-back-link">
          <ChevronLeft className="w-3.5 h-3.5" /> Research Idea Center
        </Link>

        <div className="mt-4 kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">
          {(idea.path || []).join(" › ")}
        </div>
        <h1 className="font-serif text-4xl lg:text-5xl text-kb-ink mt-2 leading-tight">{idea.title}</h1>

        <div className="mt-4 flex flex-wrap gap-2">
          <span className="kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-slate-100 text-kb-ink-muted">
            {idea.difficulty?.toUpperCase() || "INTERMEDIATE"}
          </span>
          {idea.estimated_duration_months && (
            <span className="kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-slate-100 text-kb-ink-muted inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> ~{idea.estimated_duration_months} months
            </span>
          )}
          {(idea.tags || []).map((t) => (
            <span key={t} className="kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-200 text-kb-ink-muted">{t}</span>
          ))}
          {idea.is_premium && (
            <span className="kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-kb-primary/10 text-kb-primary inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> PREMIUM
            </span>
          )}
        </div>

        {idea.overview && (
          <section className="mt-8 kb-card p-6">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">OVERVIEW</div>
            <p className="mt-3 text-base text-kb-ink leading-relaxed">{idea.overview}</p>
          </section>
        )}

        {(idea.locked || idea.has_access === false) ? (
          <section className="mt-6 kb-card p-8 text-center border-2 border-dashed border-kb-primary/40 bg-kb-primary/5" data-testid="idea-paywall">
            <Lock className="w-8 h-8 text-kb-primary mx-auto" strokeWidth={1.4} />
            <h3 className="font-serif text-2xl text-kb-ink mt-3">Unlock the full research brief</h3>
            <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
              Subscribe to read the full research gap analysis, suggested methodology, literature review,
              and download the summary PDF.
            </p>
            <Link to="/pricing" className="kb-btn-primary mt-5 inline-flex" data-testid="idea-pricing-cta">
              View pricing plans <ArrowRight className="w-4 h-4" />
            </Link>
            {idea.research_gap && (
              <div className="mt-8 text-left">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RESEARCH GAP (PREVIEW)</div>
                <p className="mt-2 text-sm text-kb-ink leading-relaxed italic">{idea.research_gap}</p>
              </div>
            )}
          </section>
        ) : (
          <>
            {idea.research_gap && (
              <section className="mt-6 kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RESEARCH GAP</div>
                <div className="mt-3 prose-kb"><MarkdownView source={idea.research_gap} /></div>
              </section>
            )}
            {idea.suggested_methodology && (
              <section className="mt-6 kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SUGGESTED METHODOLOGY</div>
                <div className="mt-3 prose-kb"><MarkdownView source={idea.suggested_methodology} /></div>
              </section>
            )}
            {idea.literature_review && (
              <section className="mt-6 kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">LITERATURE REVIEW</div>
                <div className="mt-3 prose-kb"><MarkdownView source={idea.literature_review} /></div>
              </section>
            )}
            {(idea.relevant_papers?.length > 0) && (
              <section className="mt-6 kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RELEVANT PAPERS</div>
                <ul className="mt-3 space-y-3">
                  {idea.relevant_papers.map((p, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <BookOpen className="w-4 h-4 mt-0.5 text-kb-primary shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm text-kb-ink">{p.title}</div>
                        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-0.5">
                          {p.authors}{p.year ? ` · ${p.year}` : ""}
                          {p.doi ? ` · DOI ${p.doi}` : ""}
                        </div>
                      </div>
                      {p.url && (
                        <a href={p.url} target="_blank" rel="noreferrer" className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1">
                          Open <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {idea.video_url && (
              <section className="mt-6 kb-card p-6" data-testid="idea-explainer-video">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EXPLAINER VIDEO</div>
                <div className="mt-3 aspect-video rounded-sm overflow-hidden bg-black">
                  {(() => {
                    const u = idea.video_url;
                    const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
                    const vimeo = u.match(/vimeo\.com\/(\d+)/);
                    if (yt) {
                      return <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${yt[1]}`} title="Explainer" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />;
                    }
                    if (vimeo) {
                      return <iframe className="w-full h-full" src={`https://player.vimeo.com/video/${vimeo[1]}`} title="Explainer" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />;
                    }
                    return <video src={u} controls className="w-full h-full object-cover" />;
                  })()}
                </div>
              </section>
            )}
            {(idea.summary_doc_url || idea.summary_doc_base64) && (
              <section className="mt-6 kb-card p-6 flex items-center justify-between">
                <div>
                  <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SUMMARY BRIEF</div>
                  <div className="mt-1 text-sm text-kb-ink">PDF summary of this research idea is available.</div>
                </div>
                {idea.summary_doc_url ? (
                  <a href={idea.summary_doc_url} target="_blank" rel="noreferrer" className="kb-btn-primary text-sm" data-testid="idea-summary-download">
                    <FileDown className="w-3.5 h-3.5" /> Download PDF
                  </a>
                ) : (
                  <a href={idea.summary_doc_base64} download={`${idea.title}.pdf`} className="kb-btn-primary text-sm" data-testid="idea-summary-download">
                    <FileDown className="w-3.5 h-3.5" /> Download PDF
                  </a>
                )}
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
